// PO7-S3 (docs/build-briefs/PO7-S3-invoice-review-and-variance.md). Pure
// helpers for the invoice review screen: money and percentages from the
// server's integer cents and tenths, the plain words for each flag, section
// counts, and the two parsers for what a reviewer types by hand (a dollar
// amount into cents, a quantity into a decimal string). No DOM, no network.
// Nothing here decides a flag or a total: the server derives them from the
// location's own settings, and this file only says them.

import { normalizeLeadingDot } from "../../src/domain/leadingDot.ts";
import type { InvoiceReviewFlag, InvoiceReviewLineDto } from "./api.ts";
import { formatDraftMoney } from "./invoiceDraftDisplay.ts";

/** `255` is `+$2.55`, `-255` is `-$2.55`, zero is `None` (the mockup's word),
 * and a difference that cannot be worked out says so. */
export function formatSignedMoney(cents: number | null): string {
  if (cents === null) {
    return "Not available";
  }
  if (cents === 0) {
    return "None";
  }
  return `${cents > 0 ? "+" : "-"}${formatDraftMoney(Math.abs(cents))}`;
}

/** `90` is `9.0%`, `579` is `57.9%`, `-50` is `-5.0%`. */
export function formatPercentTenths(tenths: number): string {
  const negative = tenths < 0;
  const absolute = Math.abs(tenths);
  return `${negative ? "-" : ""}${Math.floor(absolute / 10)}.${absolute % 10}%`;
}

/** The mockup's Difference cell: `+$2.55 (9.0%)`, or `None` when there is none. */
export function describeDifference(
  line: Pick<InvoiceReviewLineDto, "differenceCents" | "differencePercentTenths">,
): string {
  if (line.differenceCents === null || line.differenceCents === 0) {
    return formatSignedMoney(line.differenceCents);
  }
  const percent =
    line.differencePercentTenths === null
      ? ""
      : ` (${formatPercentTenths(line.differencePercentTenths)})`;
  return `${formatSignedMoney(line.differenceCents)}${percent}`;
}

/** 500 is `5%`, 250 is `2.5%`. */
export function formatBasisPoints(basisPoints: number): string {
  const whole = Math.floor(basisPoints / 100);
  const rest = basisPoints % 100;
  if (rest === 0) {
    return `${whole}%`;
  }
  return `${whole}.${String(rest).padStart(2, "0").replace(/0$/, "")}%`;
}

/** The sentence under the Matched heading, from the location's own numbers. */
export function describeVarianceRule(settings: {
  varianceBasisPoints: number;
  varianceFloorCents: number;
}): string {
  return (
    "Matched to a line on this order. A flagged line differs from the order cost by more than " +
    `${formatBasisPoints(settings.varianceBasisPoints)} or ${formatDraftMoney(settings.varianceFloorCents)}, whichever is greater.`
  );
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? `1 ${one}` : `${count} ${many}`;
}

export function describeMatchedCount(counts: { matched: number; flagged: number }): string {
  return `${plural(counts.matched, "line", "lines")}, ${counts.flagged} flagged`;
}

export function describeAttentionCount(count: number): string {
  return plural(count, "item", "items");
}

export function describeHeldBackCount(count: number): string {
  return `${plural(count, "line", "lines")}, not applied`;
}

/** Why a flag was raised, in words a manager can act on. */
export function describeFlag(flag: InvoiceReviewFlag, line: InvoiceReviewLineDto): string {
  switch (flag) {
    case "cost_variance": {
      const quantity = line.invoiceQuantity ?? "";
      const total =
        line.lineDifferenceCents === null
          ? ""
          : formatDraftMoney(Math.abs(line.lineDifferenceCents));
      const unit =
        line.differenceCents === null ? "" : formatDraftMoney(Math.abs(line.differenceCents));
      const direction = (line.lineDifferenceCents ?? 0) < 0 ? "under" : "over";
      return `${quantity} x ${unit} = ${total} ${direction} the order cost`;
    }
    case "received_variance":
      return `Billed ${line.invoiceQuantityOnOrderLine ?? "?"}, received ${line.receivedQuantity ?? "?"}`;
    case "no_cost_baseline":
      return "No cost on the order to compare with";
  }
}

/** The reason line under an item in Needs your attention. */
export function describeAttentionReason(line: InvoiceReviewLineDto): string {
  if (line.match.status === "uncertain") {
    return line.productName === null
      ? "Possible match, confirm it"
      : `Possible match: ${line.productName}`;
  }
  return line.match.source === "reviewer" ? "Marked unmatched" : "Unmatched item";
}

/** The reason line under an item in Held back. */
export function describeHoldBackReason(line: InvoiceReviewLineDto): string {
  return line.holdBackReason === "unreadable"
    ? "Could not read the quantity or cost"
    : "Low confidence, could not read this line clearly";
}

/** How a matched row says what it was matched on. */
export function describeMatchSource(line: InvoiceReviewLineDto): string {
  switch (line.match.source) {
    case "item_code":
      return "Matched on item code";
    case "description":
      return "Matched on description";
    case "reviewer":
      return line.productName === null ? "Matched by you" : `Matched by you to ${line.productName}`;
    case null:
      return "";
  }
}

const DOLLARS_PATTERN = /^\$?\s*(\d{1,7}|\d{1,3}(,\d{3})+)(\.\d{1,2})?$/;

/** `30.95` is 3095; `$9.50` is 950; `9` is 900. Anything that is not a whole
 * number of cents (a third decimal, a minus sign, letters) is null. The one
 * place a person's typed amount becomes cents, and only for an external fact
 * read off a vendor's document. */
export function parseDollarsToCents(text: string): number | null {
  const trimmed = text.trim();
  if (!DOLLARS_PATTERN.test(trimmed)) {
    return null;
  }
  const digits = trimmed.replace(/[$,\s]/g, "");
  const [whole, fraction = ""] = digits.split(".");
  return Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
}

const QUANTITY_PATTERN = /^\d{1,8}(\.\d{1,4})?$/;

/** A positive decimal with at most four places, or null. */
export function parseQuantityText(text: string): string | null {
  const trimmed = normalizeLeadingDot(text);
  if (!QUANTITY_PATTERN.test(trimmed) || Number(trimmed) <= 0) {
    return null;
  }
  return trimmed;
}
